import { StrictMode, lazy, Suspense, useState, useEffect, useCallback } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import App from './App'
import { useMailStore } from './stores/mailStore'
import { useAccountStore } from './stores/accountStore'
import { setAccessToken } from './lib/api'
import './styles/globals.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,       // 30s — mail doesn't need constant refetching
      gcTime: 5 * 60 * 1000,      // 5m cache
      retry: 2,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
})

// Lazy-load the ControlPanel so the mail bundle stays lean.
const LazyControlPanel = lazy(() => import('./pages/ControlPanel'))

// ─── Loading fallback ─────────────────────────────────────────────────────────
const loadingFallback = (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'var(--text-tertiary)', fontSize: 14 }}>
    Loading…
  </div>
)

// ─── ControlPanelPage ─────────────────────────────────────────────────────────
// Bootstraps auth for the CP.  When navigating from the mail app (state-based
// routing), the Zustand store already has the authenticated user — so we skip
// all network calls and render immediately.  Only when the user lands directly
// on /cp (deep link / hard reload) do we do the full token-validation flow.
function ControlPanelPage({ onBack }: { onBack: () => void }) {
  const [ready, setReady] = useState(false)
  const user = useMailStore((s) => s.user)

  // Read role + identity from the accountStore's ACTIVE account so that account
  // switches take effect immediately — mailStore.user is the primary account's
  // user and may lag behind when switching to a secondary account.
  const activeAccountEntry = useAccountStore((s) =>
    s.accounts.find(a => a.email === s.activeEmail) ?? null
  )
  const activeRole = activeAccountEntry?.user?.role ?? activeAccountEntry?.cachedRole ?? user?.role
  const displayUser = activeAccountEntry?.user ?? user

  useEffect(() => {
    // Fast path: App already authenticated — reuse its store state.
    // Use the ACTIVE account's role (not the primary mailStore user) so that
    // switching to a different account opens CP with the correct permissions.
    const storeAuth = useMailStore.getState().isAuthenticated
    const activeAccount = useAccountStore.getState().getActiveAccount()
    const activeRole = activeAccount?.user?.role ?? activeAccount?.cachedRole ?? useMailStore.getState().user?.role
    if (storeAuth && activeRole && (activeRole === 'admin' || activeRole === 'superadmin')) {
      const token = activeAccount?.accessToken ?? localStorage.getItem('access_token') ?? ''

      const isExpired = (t: string) => {
        try { const p = JSON.parse(atob(t.split('.')[1])); return p.exp * 1000 < Date.now() } catch { return true }
      }

      if (!isExpired(token)) {
        setAccessToken(token)
        setReady(true)
        return
      }

      // Token is expired — refresh it using the body RT before showing CP pages.
      const bodyRT = activeAccount?.refreshToken ?? ''
      const apiBase = (import.meta.env.VITE_API_URL ?? '').replace(/\/api\/?$/, '')
      fetch(`${apiBase}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        ...(bodyRT ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ refresh_token: bodyRT }) } : {}),
      })
        .then(r => r.ok ? r.json() : Promise.reject('refresh-failed'))
        .then(d => { setAccessToken(d.access_token); setReady(true) })
        .catch(() => { window.location.href = '/' })
      return
    }

    // Slow path: hard reload directly onto /cp — do full bootstrap.
    const token = sessionStorage.getItem('cp_access_token') || localStorage.getItem('access_token')
    sessionStorage.removeItem('cp_access_token')
    if (!token) { window.location.href = '/'; return }

    const apiBase = (import.meta.env.VITE_API_URL ?? '').replace(/\/api\/?$/, '')

    const isExpired = (t: string) => {
      try { const p = JSON.parse(atob(t.split('.')[1])); return p.exp * 1000 < Date.now() } catch { return true }
    }

    const getBodyRT = (t: string): string => {
      try {
        const email = JSON.parse(atob(t.split('.')[1])).email ?? ''
        const stored = JSON.parse(localStorage.getItem('tmail_accounts') ?? '[]') as Array<{ email: string; refreshToken?: string }>
        return stored.find(a => a.email === email)?.refreshToken ?? ''
      } catch { return '' }
    }

    const doRefresh = (t: string): Promise<string> => {
      const bodyRT = getBodyRT(t)
      return fetch(`${apiBase}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        ...(bodyRT ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ refresh_token: bodyRT }) } : {}),
      }).then(r => r.ok ? r.json() : Promise.reject('refresh-failed'))
        .then(d => { setAccessToken(d.access_token); return d.access_token })
    }

    const tokenPromise = isExpired(token) ? doRefresh(token) : Promise.resolve(token)

    tokenPromise
      .then(t => {
        setAccessToken(t)
        return fetch(`${apiBase}/api/auth/me`, {
          headers: { Authorization: `Bearer ${t}` },
          credentials: 'include',
        })
      })
      .then(r => r.ok ? r.json() : Promise.reject('me-failed'))
      .then(u => {
        if (u.role !== 'admin' && u.role !== 'superadmin') {
          window.location.href = '/'
          return
        }
        useMailStore.setState({ user: u, isAuthenticated: true })
        setReady(true)
      })
      .catch(() => { window.location.href = '/' })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const role: 'admin' | 'superadmin' =
    activeRole === 'superadmin' ? 'superadmin' : 'admin'

  if (!ready) return loadingFallback

  return (
    <Suspense fallback={loadingFallback}>
      <LazyControlPanel
        role={role}
        userName={displayUser?.name}
        userEmail={displayUser?.email}
        onBack={onBack}
      />
    </Suspense>
  )
}

// ─── Root ─────────────────────────────────────────────────────────────────────
// State-based routing: both App and ControlPanel live in the same React tree.
// Switching between them flips a boolean — no page reloads, no auth re-init.
//
// • App  is always mounted (hidden via display:none when CP is showing) so all
//   JMAP connections, stores, and cached threads are preserved across CP visits.
// • CP   is mounted on first open and stays mounted (hidden when not active)
//   so subsequent CP visits are instant without a re-bootstrap.
// • Browser back/forward (popstate) keeps the URL in sync with view state.
function Root() {
  const [showCP, setShowCP] = useState(() => window.location.pathname.startsWith('/cp'))
  // Once true, stays true — keeps CP mounted so state survives round-trips.
  const [cpEverShown, setCpEverShown] = useState(() => window.location.pathname.startsWith('/cp'))

  const openCP = useCallback(() => {
    window.history.pushState({}, '', '/cp')
    setCpEverShown(true)
    setShowCP(true)
  }, [])

  const closeCP = useCallback(() => {
    window.history.pushState({}, '', '/')
    setShowCP(false)
  }, [])

  // Allow child components (App, AccountSwitcher) to open the CP without prop drilling.
  useEffect(() => {
    const handler = () => openCP()
    window.addEventListener('open-cp', handler)
    return () => window.removeEventListener('open-cp', handler)
  }, [openCP])

  // Keep URL in sync if the user navigates with browser back/forward buttons.
  useEffect(() => {
    const handler = () => {
      const isCP = window.location.pathname.startsWith('/cp')
      if (isCP) { setCpEverShown(true); setShowCP(true) }
      else setShowCP(false)
    }
    window.addEventListener('popstate', handler)
    return () => window.removeEventListener('popstate', handler)
  }, [])

  // Pre-warm the lazy CP chunk while the user is on the mail app so the first
  // CP open doesn't stall on a chunk download.
  useEffect(() => {
    if (!showCP) {
      const timer = setTimeout(() => { import('./pages/ControlPanel') }, 2000)
      return () => clearTimeout(timer)
    }
  }, [showCP])

  return (
    <>
      {/* Mail app — always mounted, only hidden when CP is active */}
      <div style={showCP ? { display: 'none' } : undefined}>
        <App />
      </div>

      {/* Control Panel — mounted on first open, then kept alive */}
      {cpEverShown && (
        <div style={!showCP ? { display: 'none' } : undefined}>
          <ControlPanelPage onBack={closeCP} />
        </div>
      )}
    </>
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <Root />
    </QueryClientProvider>
  </StrictMode>,
)
